import { computed, ref } from 'vue';

type AsyncFunction<T extends any[], R> = (...args: T) => Promise<R>;

export const useWaiters = <T extends any[], R>(asyncFunc: AsyncFunction<T, R>, cancel?: () => void | Promise<void>) => {
  const isProcessing = ref(false);
  const processing = computed(() => isProcessing.value);
  const waiters: Array<{ resolve: (value: R) => void; reject: (reason?: unknown) => void }> = [];

  let currentPromise: Promise<R> | null = null;
  let currentArgs: T | null = null;

  const execute = (...args: T): Promise<R> => {
    // проверяем - совпадают ли аргументы при вызове.
    if (currentPromise && JSON.stringify(currentArgs) === JSON.stringify(args)) {
      return new Promise<R>((resolve, reject) => {
        waiters.push({ resolve, reject });
      });
    }

    if (currentPromise) {
      if (cancel) {
        cancel();
      }

      const error = new Error('Previous call was aborted due to new arguments');

      waiters.forEach((waiter) => waiter.reject(error));
      waiters.length = 0;
    }

    isProcessing.value = true;
    currentArgs = args;

    const clearState = () => {
      currentPromise = null;
      currentArgs = null;
      waiters.length = 0;
      isProcessing.value = false;
    };

    currentPromise = new Promise<R>((resolve, reject) => {
      waiters.push({ resolve, reject });

      (async () => {
        let result;

        try {
          result = await asyncFunc(...args);
        } catch (error) {
          waiters.forEach((waiter) => waiter.reject(error));
          clearState();
          return;
        }

        waiters.forEach((waiter) => waiter.resolve(result));
        clearState();
      })();
    });

    return currentPromise;
  };

  return {
    execute,
    processing,
  };
};
